import { List, TextField, DateField, NumberField, TextInput, DateInput, NumberInput, useDataProvider } from 'react-admin';
import Datagrid from '../Commons/Datagrid';
import PostPagination from '../Commons/PostPagination';
import { useMediaQuery, createTheme, Table, TableBody, TableCell, TableHead, TableRow } from '@mui/material';
import BreadCrumbCustom from '../Commons/BreadCrum';
import { useLocation, useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import historyTracker from '../Commons/historyTracker';
import AuthNotification from '../Commons/AuthNotification';
import { StyleTheme, Style } from '../Commons/Styles.jsx';
import { createStyles, makeStyles } from '@mui/styles';
import FilterSidebar, { ShrinkAwareSelectInput } from '../Commons/FilterSidebar';
import ListActions from '../Commons/ListActions';
import InternalMenu from '../Commons/InternalMenu';
import CircularProgress from '@mui/material/CircularProgress';
// TODO: reemplazar por el ícono definitivo de Proveedores cuando esté disponible
import providers from '../Commons/Icons/apps.svg';
import providerList from '../Commons/Icons/book.svg';

import { hasPermissions } from '../Commons/CompanyAuth';

// El filtro "provider" ya viaja codificado en la URL como ?filter={"provider":"..."} (asi
// es como se abre esta pantalla desde ProvidersShow) y <List> lo toma directo de ahi para
// pedirle al API el listado acotado a ese proveedor -- no hace falta leerlo aparte aca
// (a diferencia de ProviderInvoicesList, que si lo necesita suelto para armar el link de
// "Crear factura"; esta pantalla todavia no tiene una accion de creacion propia).

// Listado de facturas por pagar de un proveedor puntual (sp_lista_cbfactura_por_pagar,
// acotado por codigo_proveedor). Se llega aqui desde el botón "Por Pagar" de
// ProvidersShow. A diferencia de ProviderInvoicesList (que lista TODAS las facturas
// ingresadas via sp_lista_cbfactura_editable), este SP ya trae solo las que estan
// registradas ('R'/'P') con saldo pendiente > 0, con el "Pago Líquido" ya calculado.
// Por ahora es solo de consulta -- todavia no hay accion de registrar el pago.
// El breadcrumb usa una etiqueta fija ("Facturas por Pagar"): al ser un listado, no un
// registro puntual, no tiene sentido sufijarlo con el nombre del proveedor.
// Por defecto, cuando el listado devuelve 0 filas CON un filtro activo (aca "provider"
// siempre viaja como filtro implicito en la URL, ver comentario mas abajo), react-admin
// reemplaza TODO el <Datagrid> (encabezado incluido) por un mensaje generico sin traducir
// tipo "No Provider/invoicetopays found using the current filters." (viene de
// ListNoResults, en ra-ui-materialui/dist/list/datagrid/Datagrid.js -- se dispara cuando el
// propio <Datagrid> recibe la prop "empty" en undefined; el "empty={false}" que ya tiene el
// <List> de mas abajo NO alcanza para evitarlo, porque ese prop de <List> solo cubre el caso
// de "no hay NINGUN registro y ademas no hay filtro", no el de "0 resultados con un filtro
// aplicado" -- mismo mecanismo ya documentado/resuelto en AccountingExpensesList.jsx). Para
// esta pantalla se arma un reemplazo propio (mismos encabezados que la tabla real) para el
// prop "empty" del Datagrid, en vez de dejar que se vea ese mensaje en ingles con el nombre
// crudo del recurso.
const ProviderPayableInvoicesEmpty = () => (
  <Table size="small">
    <TableHead>
      <TableRow>
        <TableCell style={Style.DataGrid}>No. Factura</TableCell>
        <TableCell style={Style.DataGrid}>Fecha Factura</TableCell>
        <TableCell style={Style.DataGrid}>Monto</TableCell>
      </TableRow>
    </TableHead>
    <TableBody>
      <TableRow>
        <TableCell colSpan={3} style={{ textAlign: 'center' }}>
          No hay facturas pendientes de pago para este proveedor.
        </TableCell>
      </TableRow>
    </TableBody>
  </Table>
);

const useStyles = makeStyles(() => createStyles(StyleTheme));

const ProviderPayableInvoicesList = (props) => {
  const classes = useStyles();
  const theme = createTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const location = useLocation();
  const [openAuth, setOpenAuth] = useState(false);
  const [auth, setAuth] = useState(false);
  // Mientras esto es true se muestra el spinner de carga inicial (ver el bloque
  // "initialLoading" en el return): sin este estado la ventana se veia en blanco
  // (solo el breadcrumb) mientras se traian los permisos.
  const [initialLoading, setInitialLoading] = useState(true);
  const dataProvider = useDataProvider();

  useEffect(() => {
    (async () => {
      try {
        const currentPath = location.pathname;
        historyTracker.addPath(currentPath, 'Facturas por Pagar', 4);

        // Se reutiliza el permiso "ProviderInvoice" (mismo dato de fondo, solo que
        // filtrado a lo pendiente de pago) para no depender de un recurso nuevo en Auth0
        // que todavia no existe. Si mas adelante se agrega una accion propia de pago aca,
        // conviene revisar si conviene separar el permiso.
        const authdata = { data: hasPermissions('ProviderInvoice', 'list') };
        if (authdata.data.length > 0) {
          setAuth(authdata.data[0]);
          setOpenAuth(!authdata.data[0]);
        } else {
          setAuth(false);
          setOpenAuth(true)
        }
      }
      catch {
      }
      finally {
        setInitialLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataProvider, location.pathname]);

  // El filtro "provider" ya viaja fijo en la propia URL (ver handleRedirectPayableInvoices
  // en ProvidersShow) y no forma parte de este Filter, asi que se mantiene intacto: <List>
  // solo actualiza en filterValues las claves que este formulario controla.
  // invoiceNumber/currencyType/netPaymentFrom-To/invoiceDateStart-End no los soporta el SP
  // de forma nativa, se filtran en memoria en el propio endpoint.
  const PostFilter = [
    <TextInput InputProps={{ disableUnderline: true }} label={<span>{"No. Factura / Serie"}</span>} source="invoiceNumber" alwaysOn />,
    <ShrinkAwareSelectInput InputProps={{ disableUnderline: true }} label={<span>{"Moneda"}</span>} source="currencyType" choices={[{ id: 'ML', name: 'Local' }, { id: 'ME', name: 'Extranjera' }]} alwaysOn />,
    <NumberInput InputProps={{ disableUnderline: true }} label={<span>{"Pago Líquido Desde"}</span>} source="netPaymentFrom" alwaysOn />,
    <NumberInput InputProps={{ disableUnderline: true }} label={<span>{"Pago Líquido Hasta"}</span>} source="netPaymentTo" alwaysOn />,
    <DateInput InputProps={{ disableUnderline: true }} label={<span>{"Fecha Factura Desde"}</span>} source="invoiceDateStart" alwaysOn />,
    <DateInput InputProps={{ disableUnderline: true }} label={<span>{"Fecha Factura Hasta"}</span>} source="invoiceDateEnd" alwaysOn />,
  ];

  const handleRedirectProviderList = () => {
    navigate(`/provider`);
  };

  const [openProviders, setOpenProviders] = useState(true);

  const handleClickOpenProviders = () => {
    setOpenProviders(!openProviders);
  };

  const menus = [{
    icon: <img src={providers} alt="logo" style={Style.MenuLogo} />, name: 'Proveedores',
    open: openProviders,
    openAction: handleClickOpenProviders,
    actions: [
      { icon: <img src={providerList} alt="logo" style={Style.MenuLogo} />, name: 'Listado', onClick: handleRedirectProviderList },
    ]
  }];

  let pagination = <PostPagination menu={<InternalMenu menus={menus} />} />;

  return (
    <div class="container">
      <BreadCrumbCustom history={historyTracker.getHistory()} />
      {initialLoading ? (
        // Spinner de carga inicial: reutiliza la misma clase "Loading" (index.css) y
        // el mismo CircularProgress que ya se usa en otras pantallas para el
        // guardado, pero aqui cubre la espera de auth, que antes dejaba la
        // pantalla en blanco.
        <div class="Loading" style={{ display: 'block' }}>
          <CircularProgress size="15rem" />
        </div>
      ) : auth ? (
        <div class="row">
          <div class="col-12">
            {isSmall ? (
              <List empty={false} {...props} actions={<ListActions download />} bulkActionButtons={false} pagination={pagination}>
                <Datagrid {...props} empty={false}>
                </Datagrid>
              </List>
            ) : (
              <List empty={false} perPage={10} {...props} aside={<FilterSidebar {...props} filters={PostFilter} />} actions={<ListActions download />} bulkActionButtons={false} pagination={pagination}>
                <Datagrid {...props} style={Style.DataGrid} empty={<ProviderPayableInvoicesEmpty />}>
                  <TextField source="invoiceNumber" style={Style.fieldList} label="No. Factura" cellClassName={classes.fields} />
                  <DateField source="invoiceDate" style={Style.fieldList} label="Fecha Factura" cellClassName={classes.fields} />
                  <NumberField source="invoiceAmount" style={Style.fieldList} label="Monto" cellClassName={classes.fields} options={{ style: 'currency', currency: 'GTQ' }} />
                </Datagrid>
              </List>
            )}
          </div>
        </div>) : (<AuthNotification openerror={openAuth} severity={'error'} message={'No posee los permisos para poder interactuar con esta vista'} />)}
    </div>
  )
};

export default ProviderPayableInvoicesList;
